<template>
  <Modal class="max-w-[600px]!">
    <Modal.Title>Copyright settings</Modal.Title>
    <Modal.Body>
      <template v-if="!isOwner">
        <Alert
          v-if="isCoOwner"
          variant="warning"
          :icon="CoOwnerIcon"
          class="mb-[14px]"
        >
          You are a co-owner of this file. You can only view its settings and
          remove yourself as a co-owner.
        </Alert>

        <Alert
          v-else-if="isAuthor"
          variant="warning"
          :icon="AuthorIcon"
          class="mb-[14px]"
        >
          You are an author of this file. You can only view its settings and
          remove yourself as an author.
        </Alert>
      </template>

      <div class="mb-[14px]">
        <Input
          v-model="title"
          size="xl"
          label="File title"
          :disabled="!canEditDetails"
          :error="titleError"
        />
      </div>
      <div ref="descriptionField" class="mb-[14px]">
        <Textarea
          v-model="description"
          size="xl"
          label="Description"
          :disabled="!canEditDetails"
        />
      </div>

      <div
        class="w-full pb-[18px] border-b border-gray-800/20 flex justify-between items-center"
      >
        <div>
          <div class="font-medium text-[14px] mb-1.5">Ownership</div>
          <div class="flex items-center">
            <div class="flex items-center -ml-0.5 mr-2.5">
              <div
                v-for="(owner, index) in owners.slice(0, 3)"
                :key="owner.id"
                class="size-[30px] border-2 border-white flex items-center justify-center rounded-full"
                :class="[
                  !index && 'z-1',
                  index === 1 && 'z-0',
                  index && '-ml-[14px]',
                ]"
              >
                <Avatar :src="owner.avatarUrl" :size="26" />
              </div>
            </div>
            <Link icon="arrow" @click="handleOwnersClick">
              {{ ownersLinkCopy }}
            </Link>
          </div>
        </div>
        <Button
          variant="stroke"
          :disabled="shouldDisable"
          @click="handleOwnersClick"
        >
          {{ manageActionCopy }}
        </Button>
      </div>

      <div
        class="w-full py-[14px] border-b border-gray-800/20 flex justify-between items-center"
      >
        <div>
          <div class="font-medium text-[14px] mb-1">Authors</div>
          <div class="flex items-center">
            <div v-if="authorsCount" class="flex items-center -ml-0.5 mr-2.5">
              <div
                v-for="(author, index) in authors.slice(0, 3)"
                :key="author.id"
                class="size-[30px] border-2 border-white flex items-center justify-center rounded-full"
                :class="[
                  !index && 'z-1',
                  index === 1 && 'z-0',
                  index && '-ml-[14px]',
                ]"
              >
                <Avatar :src="author.avatarUrl" :size="26" />
              </div>
            </div>
            <div v-if="!authorsCount" class="leading-[18px]">0 authors</div>
            <Link v-else icon="arrow" @click="handleAuthorsClick">
              {{ authorsLinkCopy }}
            </Link>
          </div>
        </div>
        <Button
          variant="stroke"
          :disabled="shouldDisable"
          @click="handleAuthorsClick"
        >
          {{ manageActionCopy }}
        </Button>
      </div>

      <div
        class="w-full py-[14px] border-b border-gray-800/20 flex justify-between items-center"
      >
        <div>
          <div class="font-medium text-[14px] mb-1">Invitees</div>
          <div class="flex items-center">
            <div v-if="viewersCount" class="flex items-center -ml-0.5 mr-2.5">
              <div
                v-for="(viewer, index) in viewers.slice(0, 3)"
                :key="viewer.id"
                class="size-[30px] border-2 border-white flex items-center justify-center rounded-full"
                :class="[
                  !index && 'z-1',
                  index === 1 && 'z-0',
                  index && '-ml-[14px]',
                ]"
              >
                <Avatar :src="viewer.avatarUrl" :size="26" />
              </div>
            </div>
            <div v-if="!viewersCount" class="leading-[18px]">0 have access</div>
            <Link v-else icon="arrow" @click="handleInvitesClick">
              {{ invitesLinkCopy }}
            </Link>
          </div>
        </div>
        <Button
          variant="stroke"
          :disabled="shouldDisable"
          @click="handleInvitesClick"
        >
          Manage
        </Button>
      </div>

      <div
        class="w-full py-[14px] border-b border-gray-800/20 flex justify-between items-center"
      >
        <div>
          <div class="font-medium text-[14px] mb-1">Privacy controls</div>
          <div class="leading-[18px]">File is {{ privateStatus }}</div>
        </div>
        <Button
          v-if="isOwner"
          variant="stroke"
          :disabled="shouldDisable"
          @click="handlePrivacyClick"
        >
          Manage
        </Button>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="stroke" @click="close()">Cancel</Button>
      <Button
        variant="primary"
        :disabled="!canEditDetails"
        :loading="loading"
        @click="save"
      >
        Save
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import {
  Modal,
  Alert,
  Button,
  Input,
  Textarea,
  Avatar,
  Link,
  useModal,
} from '@dacr/library'
import { CoOwnerIcon, AuthorIcon } from '@dacr/icons'
import type { File } from '@/shared/graphql/router/graphql'
import { useFileSettings, useDescriptionFocus } from './useFileSettings'

const { close } = useModal()

const props = defineProps<{
  file: File
  focusDescription?: boolean
}>()

const descriptionField = useDescriptionFocus(() => props.focusDescription)

const {
  title,
  description,
  titleError,
  loading,
  isOwner,
  isCoOwner,
  isAuthor,
  shouldDisable,
  canEditDetails,
  manageActionCopy,
  privateStatus,
  owners,
  authors,
  viewers,
  authorsCount,
  viewersCount,
  ownersLinkCopy,
  authorsLinkCopy,
  invitesLinkCopy,
  handleOwnersClick,
  handleAuthorsClick,
  handleInvitesClick,
  handlePrivacyClick,
  save,
} = useFileSettings(props.file, close)
</script>
